import { useEffect, useRef, useState, type ReactNode } from 'react';
import { Link } from 'react-router';
import { textingApi, type BrandFacts, type CampaignFields, type NumberTextState, type RegistrationState, type RegistrationView } from '../../api/client';
import { useTenant } from '../../components/Shell';
import { Button, Card, Chip, Dialog, Field, LoadError, Loading, Notice, PageHeader, SelectField, TextAreaField } from '../../components/ui';
import { shortWhen, type Tone } from '../../lib/format';
import { Icon } from '../../lib/icons';
import { useData } from '../../lib/useData';

/**
 * Texting registration (1.13.0, Teampace 3073; help: texting-registration): the company registers for
 * 10DLC through Stitch's carrier, with Leland's help. Five steps (the business, its website, the
 * campaign, the checks, submit), then the status, Leland's fix when the carriers turn it down, and
 * each number's texting status. Admins only.
 */

export const STATE_WORDS: Record<RegistrationState, { label: string; tone: Tone }> = {
  draft: { label: 'Not submitted', tone: 'neutral' },
  brand_pending: { label: 'Brand submitted', tone: 'warn' },
  in_review: { label: 'With the carriers', tone: 'warn' },
  approved: { label: 'Approved', tone: 'ok' },
  rejected: { label: 'Turned down', tone: 'crit' },
  retired: { label: 'Retired', tone: 'neutral' },
  failed: { label: 'Didn’t go through', tone: 'crit' }
};

export const NUMBER_WORDS: Record<NumberTextState, { label: string; tone: Tone }> = {
  approved: { label: 'Approved', tone: 'ok' },
  allowed: { label: 'Allowed by Stitch', tone: 'ok' },
  p2p: { label: 'Person to person (P2P)', tone: 'ok' },
  waiting: { label: 'Waiting for approval', tone: 'warn' },
  not_registered: { label: 'Not registered', tone: 'neutral' },
  rejected: { label: 'Turned down', tone: 'crit' },
  personal: { label: 'Personal route', tone: 'neutral' }
};

const USE_CASES = [
  { value: 'CUSTOMER_CARE', label: 'Customer care (team numbers)' },
  { value: 'LOW_VOLUME', label: 'Low volume mixed (every number, fewest texts)' },
  { value: 'MIXED', label: 'Mixed' }
];

const money = (n: number) => `$${n.toFixed(2).replace(/\.00$/, '')}`;

export function TextingRegistrationPage() {
  const { tenant } = useTenant();
  const data = useData(() => textingApi.registration(tenant.domain), [tenant.domain], { live: false });
  const lists = useData(() => textingApi.verticals(tenant.domain), [tenant.domain], { live: false });
  const [view, setView] = useState<RegistrationView | null>(null);
  const [busy, setBusy] = useState('');
  const [error, setError] = useState('');
  const [note, setNote] = useState('');
  useEffect(() => {
    if (data.data) setView(data.data);
  }, [data.data]);

  if (data.error) return <LoadError error={data.error} retry={data.reload} />;
  if (!view) return <Loading label="Loading texting registration" />;
  const r = view.registration;
  const words = STATE_WORDS[r.state];

  async function run(what: string, fn: () => Promise<RegistrationView>, done?: string) {
    setBusy(what);
    setError('');
    setNote('');
    try {
      const next = await fn();
      setView(next);
      if (done) setNote(done);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'That didn’t work.');
    } finally {
      setBusy('');
    }
  }

  return (
    <div className="page texting-reg">
      <PageHeader
        eyebrow={<Link to={`/t/${tenant.domain}/settings`}>Settings</Link>}
        title="Texting registration"
        chips={<Chip tone={words.tone}>{words.label}</Chip>}
        actions={
          r.state === 'in_review' || r.state === 'brand_pending' || r.state === 'approved' ? (
            <Button icon="refresh" disabled={Boolean(busy)} onClick={() => void run('check', () => textingApi.check(tenant.domain), 'Checked with the carrier.')}>
              Check now
            </Button>
          ) : undefined
        }
      />
      <p className="muted settings-lede">
        Carriers block business texts from numbers that aren’t registered. {tenant.name} registers once: a brand (the business) and a campaign (how and why it texts). Numbers
        that text as a team (ring groups and queues) go on the campaign. Leland writes it with you and checks it against the reasons campaigns are usually turned down.
      </p>
      {error && (
        <Notice tone="crit" title="Not yet">
          {error}
        </Notice>
      )}
      {note && <Notice tone="ok">{note}</Notice>}
      {r.held && (
        <Notice tone="warn" title="Held by Stitch support">
          {r.heldReason ?? 'Stitch support is holding this registration. Ask them before submitting.'}
        </Notice>
      )}

      <StatusCard view={view} />

      {r.state === 'rejected' && (
        <FixCard
          view={view}
          busy={busy}
          onFix={() => void run('fix', () => textingApi.fix(tenant.domain))}
          onResubmit={(useFix) => void run('resubmit', () => textingApi.resubmit(tenant.domain, useFix), 'Sent again. The carriers usually decide within a few business days.')}
        />
      )}

      {view.editable ? (
        <ol className="steps reg-steps">
          <BusinessStep view={view} domain={tenant.domain} lists={lists.data ?? null} busy={busy} run={run} />
          <WebsiteStep view={view} domain={tenant.domain} busy={busy} run={run} />
          <CampaignStep view={view} domain={tenant.domain} busy={busy} run={run} />
          <ChecksStep view={view} />
          <SubmitStep view={view} domain={tenant.domain} busy={busy} run={run} />
        </ol>
      ) : (
        r.state !== 'rejected' && <CampaignSummary campaign={r.campaign} />
      )}

      <NumbersCard view={view} domain={tenant.domain} onView={setView} />
      <HistoryCard view={view} />
    </div>
  );
}

function StatusCard({ view }: { view: RegistrationView }) {
  const r = view.registration;
  const lines: string[] = [];
  if (r.state === 'draft') lines.push('Fill in the steps below, then submit. Nothing is sent, and nothing costs money, until you press Submit.');
  if (r.state === 'failed') lines.push(`It didn’t go through: ${r.error ?? 'the carrier refused it'}. Fix what it says and submit again.`);
  if (r.state === 'brand_pending') lines.push('The brand is with the registry. Stitch submits the campaign as soon as the carrier lists the brand.');
  if (r.state === 'in_review') lines.push(`The carriers are reviewing it${r.submittedAt ? ` (submitted ${shortWhen(r.submittedAt)})` : ''}. This usually takes a few business days. Stitch checks every hour and tells you when it changes.`);
  if (r.state === 'approved') lines.push(`Approved${r.decidedAt ? ` ${shortWhen(r.decidedAt)}` : ''}. Team numbers can text${r.dailyLimit ? `, up to ${r.dailyLimit.toLocaleString('en-US')} texts a day` : ''}.`);
  if (r.state === 'rejected') lines.push(`The carriers turned it down${r.decidedAt ? ` ${shortWhen(r.decidedAt)}` : ''}: ${r.rejectReason ?? 'no reason given'}.`);
  if (r.state === 'retired') lines.push('This campaign was retired. Start a new one below.');
  return (
    <Card title="Where it stands">
      <div className="card-body">
        {lines.map((l) => (
          <p key={l}>{l}</p>
        ))}
        {(r.campaignId || r.checkedAt) && (
          <p className="muted small">
            {r.campaignId ? `Campaign ${r.crid ?? r.campaignId}` : ''}
            {r.carrierStatus ? ` · carrier status ${r.carrierStatus}` : ''}
            {r.checkedAt ? ` · checked ${shortWhen(r.checkedAt)}` : ''}
            {r.submissions > 1 ? ` · submitted ${r.submissions} times` : ''}
            {r.imported ? ' · registered by hand, linked by Stitch support' : ''}
          </p>
        )}
      </div>
    </Card>
  );
}

function FixCard({ view, busy, onFix, onResubmit }: { view: RegistrationView; busy: string; onFix: () => void; onResubmit: (useFix: boolean) => void }) {
  const fix = view.registration.fix;
  const changed = fix ? (Object.keys(fix.campaign) as (keyof CampaignFields)[]) : [];
  return (
    <Card title="Leland’s fix">
      <div className="card-body stack">
        {!fix ? (
          <>
            <p>Leland hasn’t looked at this rejection yet.</p>
            <div>
              <Button variant="primary" icon="spark" disabled={Boolean(busy)} onClick={onFix}>
                Ask Leland for a fix
              </Button>
            </div>
          </>
        ) : (
          <>
            <p>{fix.explanation}</p>
            {fix.siteChanges.length > 0 && (
              <div>
                <strong>On your website first</strong>
                <ul className="reg-list">
                  {fix.siteChanges.map((s) => (
                    <li key={s}>
                      <Paste text={s} />
                    </li>
                  ))}
                </ul>
              </div>
            )}
            {changed.length > 0 && (
              <div>
                <strong>What changes in the campaign</strong>
                {changed.map((k) => (
                  <div key={k} className="reg-change">
                    <small className="muted">{FIELD_NAMES[k] ?? k}</small>
                    {Array.isArray(fix.campaign[k]) ? (
                      <ol>
                        {(fix.campaign[k] as string[]).map((x) => (
                          <li key={x}>{x}</li>
                        ))}
                      </ol>
                    ) : (
                      <p>{String(fix.campaign[k])}</p>
                    )}
                  </div>
                ))}
              </div>
            )}
            <div className="row-actions">
              <Button variant="primary" icon="send" disabled={Boolean(busy) || !view.canResubmit} onClick={() => onResubmit(true)}>
                {changed.length ? 'Use Leland’s fix and resubmit' : 'Resubmit'}
              </Button>
              <small className="muted">Each submission costs the carriers’ review fee ({money(view.fees.campaignReview)}).</small>
            </div>
          </>
        )}
      </div>
    </Card>
  );
}

const FIELD_NAMES: Partial<Record<keyof CampaignFields, string>> = {
  useCase: 'Use case',
  description: 'Description',
  callToAction: 'How people opt in',
  samples: 'Sample messages',
  startReply: 'Reply to START',
  stopReply: 'Reply to STOP',
  helpReply: 'Reply to HELP'
};

type Run = (what: string, fn: () => Promise<RegistrationView>, done?: string) => Promise<void>;

function StepShell({ n, ok, title, children }: { n: number; ok: boolean; title: string; children: ReactNode }) {
  return (
    <li className="step">
      <span className={`step-num${ok ? ' ok' : ''}`}>{ok ? <Icon name="check" size={14} /> : n}</span>
      <div className="stack">
        <strong>{title}</strong>
        {children}
      </div>
    </li>
  );
}

function BusinessStep({ view, domain, lists, busy, run }: { view: RegistrationView; domain: string; lists: { verticals: { id: string; name: string }[]; companyTypes: { id: string; name: string }[] } | null; busy: string; run: Run }) {
  const r = view.registration;
  const [f, setF] = useState<BrandFacts>(r.facts);
  const [ein, setEin] = useState('');
  const seen = useRef(r.facts);
  useEffect(() => {
    if (seen.current !== r.facts) {
      seen.current = r.facts;
      setF(r.facts);
    }
  }, [r.facts]);
  const set = (k: keyof BrandFacts) => (e: { target: { value: string } }) => setF({ ...f, [k]: e.target.value });
  const ok = !view.preflight.some((l) => ['legal-name', 'company-type', 'ein', 'vertical', 'address', 'po-box', 'contact', 'email'].includes(l.id) && !l.ok && l.level === 'fail');
  return (
    <StepShell n={1} ok={ok} title="Your business">
      <p className="muted small">Exactly as the IRS has it: reviewers compare the legal name and EIN with the IRS record, and the address with the post office’s.</p>
      <div className="form-grid">
        <Field label="Legal name (as on your EIN letter)" value={f.legalName} onChange={set('legalName')} placeholder="Acme Dental LLC" />
        <Field label="Name customers know (DBA)" value={f.dba} onChange={set('dba')} hint="Shown at the start of every text." />
        <SelectField
          label="Company type"
          value={f.companyType}
          onChange={set('companyType')}
          options={[{ value: '', label: 'Choose…' }, ...(lists?.companyTypes ?? []).map((t) => ({ value: t.id, label: t.name }))]}
        />
        <SelectField label="Industry" value={f.vertical} onChange={set('vertical')} options={[{ value: '', label: 'Choose…' }, ...(lists?.verticals ?? []).map((v) => ({ value: v.id, label: v.name }))]} />
        {f.companyType !== 'SOLE_PROPRIETOR' && (
          <Field
            label="EIN (federal tax ID)"
            value={ein}
            onChange={(e) => setEin(e.target.value)}
            placeholder={r.hasEin ? `Saved, ending ${r.einLast4 ?? '••••'}` : '12-3456789'}
            autoComplete="off"
            hint="Kept sealed. Only its last four digits are shown again."
          />
        )}
        <Field label="Website" value={f.website} onChange={set('website')} placeholder="https://acmedental.com" />
        <Field label="Business email" value={f.email} onChange={set('email')} hint="An address at your website’s domain is best." />
        <Field label="Business phone" value={f.phone} onChange={set('phone')} />
        <Field label="Contact first name" value={f.firstName} onChange={set('firstName')} />
        <Field label="Contact last name" value={f.lastName} onChange={set('lastName')} />
        <Field label="Street address" value={f.address1} onChange={set('address1')} />
        <Field label="Suite (optional)" value={f.address2} onChange={set('address2')} />
        <Field label="City" value={f.city} onChange={set('city')} />
        <Field label="State" value={f.state} onChange={set('state')} maxLength={2} placeholder="MD" />
        <Field label="ZIP" value={f.zip} onChange={set('zip')} />
        {f.companyType === 'PUBLIC_PROFIT' && (
          <>
            <Field label="Stock symbol" value={f.stockSymbol} onChange={set('stockSymbol')} />
            <Field label="Exchange" value={f.stockExchange} onChange={set('stockExchange')} placeholder="NASDAQ" />
          </>
        )}
      </div>
      <div>
        <Button
          variant="primary"
          disabled={Boolean(busy)}
          onClick={() =>
            void run(
              'facts',
              async () => {
                const next = await textingApi.save(domain, { facts: f, ...(ein.trim() ? { ein: ein.trim() } : {}) });
                setEin('');
                return next;
              },
              'Saved.'
            )
          }
        >
          Save
        </Button>
      </div>
    </StepShell>
  );
}

function WebsiteStep({ view, domain, busy, run }: { view: RegistrationView; domain: string; busy: string; run: Run }) {
  const w = view.registration.website;
  const ok = Boolean(w && w.checks.length && w.checks.every((c) => c.ok || c.id === 'name' || c.id === 'contact'));
  return (
    <StepShell n={2} ok={ok} title="Your website">
      <p className="muted small">Reviewers read your website. It needs a privacy policy that says mobile information isn’t shared, and texting terms. Leland gives you the words for anything missing.</p>
      {w && (
        <ul className="reg-checks">
          {w.checks.map((c) => (
            <li key={c.id} className={c.ok ? 'ok' : 'bad'}>
              <Icon name={c.ok ? 'check' : 'alert'} size={16} />
              <span className="cell-stack">
                <span>{c.label}</span>
                <small className="muted">{c.detail}</small>
                {c.paste && <Paste text={c.paste} />}
              </span>
            </li>
          ))}
        </ul>
      )}
      <div className="row-actions">
        <Button disabled={Boolean(busy) || !view.websiteCheck || !view.registration.facts.website} onClick={() => void run('website', () => textingApi.website(domain))}>
          {w ? 'Check again' : 'Check my website'}
        </Button>
        {w && <small className="muted">Checked {shortWhen(w.at)}</small>}
        {!view.registration.facts.website && <small className="muted">Enter your website in step 1 first.</small>}
      </div>
    </StepShell>
  );
}

function CampaignStep({ view, domain, busy, run }: { view: RegistrationView; domain: string; busy: string; run: Run }) {
  const r = view.registration;
  const [c, setC] = useState<CampaignFields>(r.campaign);
  const [covers, setCovers] = useState(r.covers);
  const seen = useRef(r.campaign);
  useEffect(() => {
    if (seen.current !== r.campaign) {
      seen.current = r.campaign;
      setC(r.campaign);
    }
  }, [r.campaign]);
  const drafted = Boolean(r.campaign.description);
  const samples = [...c.samples, '', '', '', '', ''].slice(0, Math.max(3, Math.min(5, c.samples.length + 1)));
  const why = (k: string) => (r.notes[k] ? <span className="reg-why">Leland: {r.notes[k]}</span> : undefined);
  const ok = drafted && !view.preflight.some((l) => !['legal-name', 'company-type', 'ein', 'vertical', 'address', 'po-box', 'contact', 'email', 'email-domain', 'legal-suffix'].includes(l.id) && !l.id.startsWith('site') && !l.ok && l.level === 'fail');
  return (
    <StepShell n={3} ok={ok} title="The campaign">
      <div className="radio-cards">
        <label className="radio-card">
          <input type="radio" checked={covers === 'team'} onChange={() => setCovers('team')} />
          <span className="cell-stack">
            <span>Team numbers only</span>
            <small className="muted">Numbers whose texts go to a ring group or queue. People’s own numbers wait on the carrier’s route for employee lines.</small>
          </span>
        </label>
        <label className="radio-card">
          <input type="radio" checked={covers === 'all'} onChange={() => setCovers('all')} />
          <span className="cell-stack">
            <span>Every number, people’s own too</span>
            <small className="muted">One low-volume campaign for all of them. Best for small companies.</small>
          </span>
        </label>
      </div>
      <div className="row-actions">
        <Button
          variant={drafted ? 'secondary' : 'primary'}
          icon="spark"
          disabled={Boolean(busy)}
          onClick={() =>
            void run('draft', async () => {
              if (covers !== r.covers) await textingApi.save(domain, { covers });
              return textingApi.draft(domain);
            })
          }
        >
          {drafted ? 'Write it again with Leland' : 'Write it with Leland'}
        </Button>
        {busy === 'draft' && <small className="muted">Leland is writing…</small>}
      </div>
      {drafted && (
        <>
          <SelectField label="Use case" value={c.useCase} onChange={(e) => setC({ ...c, useCase: e.target.value as CampaignFields['useCase'] })} options={USE_CASES} hint={why('useCase')} />
          <TextAreaField label="Description" rows={3} value={c.description} onChange={(e) => setC({ ...c, description: e.target.value })} hint={why('description')} />
          <TextAreaField label="How people opt in" rows={4} value={c.callToAction} onChange={(e) => setC({ ...c, callToAction: e.target.value })} hint={why('callToAction')} />
          {samples.map((m, i) => (
            <TextAreaField
              key={i}
              label={`Sample message ${i + 1}${i >= 2 ? ' (optional)' : ''}`}
              rows={2}
              value={m}
              onChange={(e) => {
                const next = [...samples];
                next[i] = e.target.value;
                setC({ ...c, samples: next.filter((x, j) => x.trim() || j < 2) });
              }}
              hint={i === 0 ? why('samples') : `${m.length} characters`}
            />
          ))}
          <TextAreaField label="Reply to START" rows={2} value={c.startReply} onChange={(e) => setC({ ...c, startReply: e.target.value })} hint={why('startReply')} />
          <TextAreaField label="Reply to STOP" rows={2} value={c.stopReply} onChange={(e) => setC({ ...c, stopReply: e.target.value })} hint={why('stopReply')} />
          <TextAreaField label="Reply to HELP" rows={2} value={c.helpReply} onChange={(e) => setC({ ...c, helpReply: e.target.value })} hint={why('helpReply')} />
          <div>
            <Button variant="primary" disabled={Boolean(busy)} onClick={() => void run('campaign', () => textingApi.save(domain, { campaign: { ...c, samples: c.samples.filter((x) => x.trim()) }, covers }), 'Saved.')}>
              Save the campaign
            </Button>
          </div>
        </>
      )}
    </StepShell>
  );
}

function ChecksStep({ view }: { view: RegistrationView }) {
  const failing = view.preflight.filter((l) => !l.ok);
  return (
    <StepShell n={4} ok={view.ready} title="Leland’s checks">
      <p className="muted small">The reasons campaigns are most often turned down. Every red line has to pass before you can submit.</p>
      <ul className="reg-checks">
        {failing.map((l) => (
          <li key={l.id} className={l.level === 'fail' ? 'bad' : 'warn'}>
            <Icon name={l.level === 'fail' ? 'alert' : 'info'} size={16} />
            <span className="cell-stack">
              <span>{l.label}</span>
              <small className="muted">{l.fix}</small>
            </span>
          </li>
        ))}
      </ul>
      {!failing.length && <p>Everything passes ({view.preflight.length} checks).</p>}
    </StepShell>
  );
}

function SubmitStep({ view, domain, busy, run }: { view: RegistrationView; domain: string; busy: string; run: Run }) {
  const fees = view.fees;
  const monthly = view.registration.campaign.useCase === 'LOW_VOLUME' ? fees.monthlyLowVolume : fees.monthlyStandard;
  return (
    <StepShell n={5} ok={false} title="Submit">
      <p>
        The registry charges {money(fees.brand)} for the brand and {money(fees.campaignReview)} for each campaign review, then {money(monthly)} a month for the campaign. {fees.note}
      </p>
      {!view.carrier && <Notice tone="warn">Texting registration needs Stitch’s carrier set up. Ask Stitch support.</Notice>}
      {view.carrier && !view.carrier.spending && <Notice tone="warn">Registering costs money, so it waits for Stitch support to turn spending on.</Notice>}
      <div className="row-actions">
        <Button
          variant="primary"
          icon="send"
          disabled={Boolean(busy) || !view.canSubmit}
          onClick={() => void run('submit', () => textingApi.submit(domain), 'Submitted. Stitch checks with the carrier every hour and tells you when it’s decided.')}
        >
          Submit to the carriers
        </Button>
        {!view.ready && <small className="muted">Fix the red lines in step 4 first.</small>}
      </div>
    </StepShell>
  );
}

function CampaignSummary({ campaign }: { campaign: CampaignFields }) {
  if (!campaign.description) return null;
  return (
    <Card title="The campaign">
      <div className="card-body stack">
        <details>
          <summary>What was registered</summary>
          <dl className="reg-summary">
            <dt>Description</dt>
            <dd>{campaign.description}</dd>
            <dt>How people opt in</dt>
            <dd>{campaign.callToAction}</dd>
            <dt>Sample messages</dt>
            <dd>
              <ol>
                {campaign.samples.map((s) => (
                  <li key={s}>{s}</li>
                ))}
              </ol>
            </dd>
            <dt>Replies</dt>
            <dd>
              START: {campaign.startReply}
              <br />
              STOP: {campaign.stopReply}
              <br />
              HELP: {campaign.helpReply}
            </dd>
          </dl>
        </details>
      </div>
    </Card>
  );
}

/**
 * People's own numbers that could text person to person (P2P): not a team's, not on already, not on the
 * approved campaign. A number allowed anyway can still move to P2P (the carrier's proper route for it).
 */
const canP2p = (n: RegistrationView['numbers'][number]) => !n.team && !n.p2p && n.state !== 'approved';

function NumbersCard({ view, domain, onView }: { view: RegistrationView; domain: string; onView: (v: RegistrationView) => void }) {
  // The numbers to turn P2P on for, once the admin agrees (one, or every person's own number).
  const [asking, setAsking] = useState<string[] | null>(null);
  const [agreed, setAgreed] = useState(false);
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(0);
  const [error, setError] = useState('');
  if (!view.numbers.length) return null;
  const open = view.numbers.filter(canP2p);
  const labelOf = (number: string) => view.numbers.find((n) => n.number === number)?.label ?? number;

  function ask(numbers: string[]) {
    setAsking(numbers);
    setAgreed(false);
    setDone(0);
    setError('');
  }
  async function turnOn() {
    if (!asking) return;
    setBusy(true);
    setError('');
    let n = 0;
    try {
      for (const number of asking) {
        onView(await textingApi.turnOnP2p(domain, number));
        n += 1;
        setDone(n);
      }
      setAsking(null);
    } catch (err) {
      const why = err instanceof Error ? err.message : 'That didn’t work.';
      setError(asking.length > 1 ? `${labelOf(asking[n] ?? '')}: ${why}` : why);
      setAsking(asking.slice(n));
    } finally {
      setBusy(false);
    }
  }

  return (
    <Card
      title="Your numbers"
      action={
        open.length > 1 ? (
          <Button size="small" variant="ghost" onClick={() => ask(open.map((n) => n.number))}>
            Turn on P2P for every person’s number ({open.length})
          </Button>
        ) : undefined
      }
    >
      <p className="muted settings-lede">
        A person’s own number texts person to person (P2P): only that person’s own conversations. Numbers that text for a team go on the campaign above.
      </p>
      {error && !asking && (
        <Notice tone="crit" title="Not turned on">
          {error}
        </Notice>
      )}
      <div className="table-wrap">
        <table className="table">
          <thead>
            <tr>
              <th>Number</th>
              <th>Texts go to</th>
              <th>Texting</th>
            </tr>
          </thead>
          <tbody>
            {view.numbers.map((n) => (
              <tr key={n.number}>
                <td>{n.label}</td>
                <td>{n.where}</td>
                <td>
                  <span className="cell-stack">
                    <Chip tone={NUMBER_WORDS[n.state].tone} small>
                      {NUMBER_WORDS[n.state].label}
                    </Chip>
                    {!n.canSend && n.reason && <small className="muted">{n.reason}</small>}
                    {n.assignError && <small className="muted">The carrier said: {n.assignError}</small>}
                    {canP2p(n) && (
                      <Button size="small" variant="ghost" onClick={() => ask([n.number])}>
                        Turn on P2P
                      </Button>
                    )}
                  </span>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <Dialog
        open={Boolean(asking)}
        onClose={() => !busy && setAsking(null)}
        title="Turn on person-to-person texting (P2P)"
        subtitle={asking && asking.length === 1 ? labelOf(asking[0]!) : `${asking?.length ?? 0} people’s own numbers`}
        foot={
          <>
            <Button variant="ghost" disabled={busy} onClick={() => setAsking(null)}>
              Cancel
            </Button>
            <Button variant="primary" disabled={!agreed || busy} onClick={() => void turnOn()}>
              {busy ? `Turning on${asking && asking.length > 1 ? ` (${done} of ${asking.length})` : '…'}` : 'Turn on P2P'}
            </Button>
          </>
        }
      >
        <p>P2P is for one person’s own conversations with the people they text. On a P2P number:</p>
        <ul className="reg-list">
          <li>Only the person who has the number writes and sends its texts.</li>
          <li>No marketing, no promotions, and no texts sent to many people at once.</li>
          <li>No automated texts: Stitch sends none from it, apart from the replies to STOP, START and HELP.</li>
          <li>Texts for a team (a ring group or queue) go from the team’s number instead.</li>
        </ul>
        <Notice tone="warn">The carrier checks P2P numbers automatically and disconnects a number that sends anything else.</Notice>
        {error && (
          <Notice tone="crit" title="Not turned on">
            {error}
          </Notice>
        )}
        <label className="check-row">
          <input type="checkbox" checked={agreed} disabled={busy} onChange={(e) => setAgreed(e.target.checked)} />
          <span>{asking && asking.length > 1 ? 'We agree to use these numbers only for person-to-person texts.' : 'We agree to use this number only for person-to-person texts.'}</span>
        </label>
      </Dialog>
    </Card>
  );
}

const EVENT_WORDS: Record<string, string> = {
  ein_saved: 'EIN saved',
  ein_removed: 'EIN removed',
  website_checked: 'Website checked',
  campaign_drafted: 'Campaign written',
  brand_submitted: 'Brand submitted',
  campaign_submitted: 'Campaign submitted',
  submit_failed: 'Submitting didn’t go through',
  approved: 'Approved',
  rejected: 'Turned down',
  fix_proposed: 'Leland proposed a fix',
  campaign_resubmitted: 'Resubmitted',
  resubmit_failed: 'Resubmitting didn’t go through',
  number_added: 'Number put on the campaign',
  number_allowed: 'Number allowed by Stitch',
  number_allow_removed: 'Number no longer allowed by Stitch',
  number_p2p: 'Person-to-person texting (P2P) turned on for a number',
  number_p2p_removed: 'Person-to-person texting (P2P) turned off for a number',
  held: 'Held by Stitch support',
  released: 'Released by Stitch support',
  retired: 'Retired',
  imported: 'Linked to a registration made by hand',
  in_review: 'Back with the carriers'
};

function HistoryCard({ view }: { view: RegistrationView }) {
  if (!view.events.length) return null;
  return (
    <Card title="What happened">
      <ul className="card-body reg-history">
        {view.events.map((e) => (
          <li key={e.id}>
            <span>{EVENT_WORDS[e.kind] ?? e.kind}</span>
            {typeof e.detail.number === 'string' && <span className="muted"> {e.detail.number}</span>}
            {typeof e.detail.error === 'string' && <span className="muted">: {e.detail.error}</span>}
            <small className="muted">
              {' '}
              · {e.byName} · {shortWhen(e.at)}
            </small>
          </li>
        ))}
      </ul>
    </Card>
  );
}

/** Words to paste on the website, with Copy. */
function Paste({ text }: { text: string }) {
  const [copied, setCopied] = useState(false);
  return (
    <span className="reg-paste">
      <span>{text}</span>
      <Button
        size="small"
        variant="ghost"
        icon="copy"
        onClick={() =>
          void navigator.clipboard
            ?.writeText(text)
            .then(() => setCopied(true))
            .catch(() => undefined)
        }
      >
        {copied ? 'Copied' : 'Copy'}
      </Button>
    </span>
  );
}
